import {useQuery} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useExamList} from '../../../ExamSelection/hooks/useExamList';
import {getExamsDates} from '../../services/getExamsDates';
import {UseGetExamsDatesQueryProps} from './types';

export function useGetExamsDatesQuery({enabled}: UseGetExamsDatesQueryProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const specialtyCode = bookingData?.specialtyCode;
	const unitCode = bookingData?.unitCode;

	const {
		examListRaw: examsList,
		isFetchingExamsList,
		examsListIsError,
		refetchExamsList,
	} = useExamList();

	const procedureCode = examsList?.tiposExames?.find(
		exam => exam.cdTipoExame === specialtyCode,
	)?.cdProcedimento;

	const {
		data: examsDates,
		isFetching: examsDatesIsFetching,
		isError: examsDatesIsError,
		error: examsDatesError,
		refetch: examsDatesRefetch,
	} = useQuery({
		queryKey: [QUERY_KEYS.EXAMS_DATES, procedureCode, unitCode, specialtyCode],
		enabled: enabled && !!examsList && !isFetchingExamsList,
		retry: 1,
		queryFn: async ({signal}) => {
			if (!procedureCode) {
				return [];
			}

			const dates = await getExamsDates({
				signal,
				cdPrestadorJuridico: unitCode,
				cdProcedimento: procedureCode,
				cdTipoExame: Number(specialtyCode),
			});

			return dates;
		},
		staleTime: 0,
		gcTime: 0,
	});

	const refetch = () => {
		examsDatesRefetch();
		refetchExamsList();
	};

	return {
		data: examsDates,
		isFetching: isFetchingExamsList || examsDatesIsFetching,
		isError: examsListIsError || examsDatesIsError,
		error: examsDatesError,
		refetch,
	};
}
